import { useMemo } from 'react';
import { ScheduleRequestEvent } from '@frontend/schedule-helpers';
import { PRIMARY_EVENT_COLOR, REQUEST_EVENT_BACKGROUND_COLOR } from '../../constants';
import { convertScheduleOpeningToEventTime } from '../../utils';
import { useGetScheduleRequestListQueries } from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/hooks/useGetScheduleRequestListQueries';
import { useRequestedAppointmentTypes } from './use-requested-appointment-types';

type UseCalendarScheduleRequestsParams = {
  locationIds: string[];
  calendarDate: string;
};

export const useCalendarScheduleRequests = ({ locationIds, calendarDate }: UseCalendarScheduleRequestsParams) => {
  const { scheduleRequests, refetch } = useGetScheduleRequestListQueries({ locationIds, date: calendarDate });
  const { requestedAppointmentTypes } = useRequestedAppointmentTypes(scheduleRequests);

  const scheduleRequestEventsData: ScheduleRequestEvent[] = useMemo(
    () =>
      scheduleRequests
        ?.map((request) => {
          const appointmentType = requestedAppointmentTypes?.find((type) => type.id === request.appointmentTypeId);
          const { practitionerId, requestedOpenings, ...restRequestData } = request;
          const requestedProviderId = practitionerId;
          const convertedOpenings =
            requestedOpenings?.map((opening) => convertScheduleOpeningToEventTime(opening, request.duration ?? 0)) ??
            [];
          return (requestedOpenings || []).map((opening, index) => {
            const requestedProviderName = opening.assets?.providers?.[0];
            const operatoryId = opening.assets?.operatories?.[0];
            const thisOpening = convertedOpenings?.[index];
            const otherRequestedOpenings = convertedOpenings?.filter((_, i) => i !== index) ?? [];

            return {
              ...restRequestData,
              organizerId: requestedProviderId,
              providerName: requestedProviderName,
              referenceTypeId: request.appointmentTypeId,
              operatoryId,
              appointmentTypeName: appointmentType?.displayName || '',
              ...thisOpening,
              displayOptions: {
                primaryColor: appointmentType?.details?.clr || PRIMARY_EVENT_COLOR,
                backgroundColor: REQUEST_EVENT_BACKGROUND_COLOR,
              },
              request: {
                ...request,
                allRequestedOpenings: convertedOpenings,
                otherRequestedOpenings,
                appointmentTypeDetails: appointmentType,
              },
              type: 'request' as const,
            };
          });
        })
        .flat() || [],
    [scheduleRequests, requestedAppointmentTypes]
  );

  return {
    scheduleRequests,
    requestedAppointmentTypes,
    scheduleRequestEventsData,
    refetchScheduleRequestList: refetch,
  };
};
